import 'rsuite/dist/rsuite.min.css';
import {
  Button,
  Chip,
  Divider,
  Radio,
  RadioGroup,
  Spinner,
} from '@nextui-org/react';
import { DateRangePicker } from 'rsuite';
import MultiSelectWithActions from '../../components/common/MultiSelectWithActions';
import { useTranslation } from '../../../../common/src/hooks/useTranslation';
import type { DataSource, UnitMode } from './types';

interface USDAFiltersProps {
  dataSource: DataSource;
  onDataSourceChange: (v: DataSource) => void;
  unit: UnitMode;
  onUnitChange: (v: UnitMode) => void;
  dateRange: [Date, Date];
  onDateRangeChange: (v: [Date, Date] | null) => void;
  onFetch: () => void;
  loading: boolean;
  loadingProgress: string;
  isFobLoading: boolean;
  isRetailLoading: boolean;
  isFobDisabled: boolean;
  isRetailDisabled: boolean;
  filterOptions: {
    markets: string[];
    varieties: string[];
    origins: string[];
    packages: string[];
    sizes: string[];
  };
  selectedMarkets: string[];
  selectedVarieties: string[];
  selectedOrigins: string[];
  selectedPackages: string[];
  selectedSizes: string[];
  onMarketsChange: (v: string[]) => void;
  onVarietiesChange: (v: string[]) => void;
  onOriginsChange: (v: string[]) => void;
  onPackagesChange: (v: string[]) => void;
  onSizesChange: (v: string[]) => void;
}

const ChipMultiSelect = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
  color = 'primary',
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (val: string) => void;
  onClear: () => void;
  color?: 'primary' | 'success' | 'warning' | 'secondary';
}) => {
  if (options.length === 0) return null;
  return (
    <div>
      <p className='text-xs font-semibold text-default-500 mb-1'>{label}</p>
      <div className='flex flex-wrap gap-1'>
        {options.map((v) => (
          <Chip
            key={v}
            size='sm'
            variant={selected.includes(v) ? 'solid' : 'flat'}
            color={selected.includes(v) ? color : 'default'}
            className='cursor-pointer text-[10px]'
            onClick={() => onToggle(v)}
          >
            {v}
          </Chip>
        ))}
        {selected.length > 0 && (
          <Chip size='sm' variant='flat' color='danger' className='cursor-pointer text-[10px]' onClick={onClear}>
            Clear
          </Chip>
        )}
      </div>
    </div>
  );
};

const toggle = (arr: string[], val: string) =>
  arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];

const toOptions = (arr: string[]) => arr.map((v) => ({ value: v, label: v }));

const USDAFilters = ({
  dataSource,
  onDataSourceChange,
  unit,
  onUnitChange,
  dateRange,
  onDateRangeChange,
  onFetch,
  loading,
  loadingProgress,
  isFobLoading,
  isRetailLoading,
  isFobDisabled,
  isRetailDisabled,
  filterOptions,
  selectedMarkets,
  selectedVarieties,
  selectedOrigins,
  selectedPackages,
  selectedSizes,
  onMarketsChange,
  onVarietiesChange,
  onOriginsChange,
  onPackagesChange,
  onSizesChange,
}: USDAFiltersProps) => {
  const { t } = useTranslation('usda-market');

  return (
    <div className='p-4 pt-8 h-full overflow-y-auto'>
      <div className='space-y-4'>
        {/* Data Source */}
        <div>
          <p className='text-sm text-default-500 mb-2'>{t('Fuente de Datos')}</p>
          <RadioGroup
            value={dataSource}
            onValueChange={(v) => onDataSourceChange(v as DataSource)}
            size='sm'
          >
            <Radio value='wholesale'>
              <span className='text-xs'>{t('Wholesale (Terminal)')}</span>
            </Radio>
            <Radio value='fob' isDisabled={isFobDisabled}>
              <span className='text-xs flex items-center gap-1'>
                {t('FOB (Shipping Point)')}
                {isFobLoading && <Spinner size='sm' className='scale-50' />}
              </span>
            </Radio>
            <Radio value='retail' isDisabled={isRetailDisabled}>
              <span className='text-xs flex items-center gap-1'>
                {t('Retail')}
                {isRetailLoading && <Spinner size='sm' className='scale-50' />}
              </span>
            </Radio>
          </RadioGroup>
        </div>

        <Divider />

        {/* Unit */}
        <div>
          <p className='text-sm text-default-500 mb-2'>{t('Unidad de Precio')}</p>
          <RadioGroup
            orientation='horizontal'
            value={unit}
            onValueChange={(v) => onUnitChange(v as UnitMode)}
            size='sm'
          >
            <Radio value='kg'>
              <span className='text-xs'>$/kg</span>
            </Radio>
            <Radio value='lb'>
              <span className='text-xs'>$/lb</span>
            </Radio>
          </RadioGroup>
        </div>

        <Divider />

        {/* Date Range */}
        <div>
          <p className='text-sm text-default-500 mb-2'>{t('Rango de Fechas')}</p>
          <DateRangePicker
            value={dateRange}
            onChange={onDateRangeChange}
            showOneCalendar
            placeholder={t('Seleccionar rango de fechas')}
            character=' - '
            format='MM/dd/yyyy'
            className='w-full'
            menuStyle={{ zIndex: 9999 }}
            disabled={loading}
          />
        </div>

        <Button
          color='primary'
          className='w-full'
          size='sm'
          isLoading={loading}
          onPress={onFetch}
        >
          {loading ? loadingProgress : t('Buscar Datos')}
        </Button>

        <Divider />

        {/* Data Filters */}
        <p className='text-sm text-default-500'>{t('Filtros')}</p>

        {dataSource !== 'retail' ? (
          <>
            {filterOptions.markets.length > 0 && (
              <MultiSelectWithActions
                label={t('Mercados')}
                placeholder={t('Seleccionar mercados')}
                options={toOptions(filterOptions.markets)}
                selectedKeys={selectedMarkets}
                onSelectionChange={onMarketsChange}
                size='sm'
              />
            )}

            {filterOptions.varieties.length > 0 && (
              <MultiSelectWithActions
                label={t('Variedades')}
                placeholder={t('Seleccionar variedades')}
                options={toOptions(filterOptions.varieties)}
                selectedKeys={selectedVarieties}
                onSelectionChange={onVarietiesChange}
                size='sm'
              />
            )}

            {filterOptions.origins.length > 0 && (
              <MultiSelectWithActions
                label={t('Orígenes')}
                placeholder={t('Seleccionar orígenes')}
                options={toOptions(filterOptions.origins)}
                selectedKeys={selectedOrigins}
                onSelectionChange={onOriginsChange}
                size='sm'
              />
            )}

            <ChipMultiSelect
              label={t('Paquetes')}
              options={filterOptions.packages}
              selected={selectedPackages}
              onToggle={(v) => onPackagesChange(toggle(selectedPackages, v))}
              onClear={() => onPackagesChange([])}
            />
            <ChipMultiSelect
              label={t('Tamaños')}
              options={filterOptions.sizes}
              selected={selectedSizes}
              onToggle={(v) => onSizesChange(toggle(selectedSizes, v))}
              onClear={() => onSizesChange([])}
            />
          </>
        ) : (
          <>
            {filterOptions.markets.length > 0 && (
              <MultiSelectWithActions
                label={t('Regiones')}
                placeholder={t('Seleccionar regiones')}
                options={toOptions(filterOptions.markets)}
                selectedKeys={selectedMarkets}
                onSelectionChange={onMarketsChange}
                size='sm'
              />
            )}

            {filterOptions.varieties.length > 0 && (
              <MultiSelectWithActions
                label={t('Variedades')}
                placeholder={t('Seleccionar variedades')}
                options={toOptions(filterOptions.varieties)}
                selectedKeys={selectedVarieties}
                onSelectionChange={onVarietiesChange}
                size='sm'
              />
            )}

            <ChipMultiSelect
              label={t('Tamaños')}
              options={filterOptions.sizes}
              selected={selectedSizes}
              onToggle={(v) => onSizesChange(toggle(selectedSizes, v))}
              onClear={() => onSizesChange([])}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default USDAFilters;
